// The quote flow's modal shell — the help-me-decide quiz today, call
// scheduling and the can't-issue interrupt tomorrow. A centered card on
// desktop; on mobile it takes the whole screen. Either way the header
// is pinned and the body scrolls.

import { useEffect, type ElementType, type ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface FlowModalProps {
  icon: ElementType;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export default function FlowModal({
  icon: Icon,
  title,
  onClose,
  children,
}: FlowModalProps) {
  // The page behind the overlay holds still while the modal is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch justify-center bg-soil/45 sm:items-center sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // A press on the backdrop dismisses — mousedown, not click, so a
      // text-selection drag that ends outside the card doesn't close it.
      // On mobile the card fills the screen and there is no backdrop.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex w-full flex-col overflow-hidden bg-ivory-light sm:max-h-[88vh] sm:max-w-[620px] sm:rounded-2xl sm:shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-oat-200 px-4 py-5 2xs:px-5 sm:px-6">
          <div className="flex items-center gap-2.5">
            <Icon
              className="h-[22px] w-[22px] text-evergreen"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-[13px] font-bold uppercase tracking-[0.12em] text-evergreen">
              {title}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-oat-50 text-soil transition-colors hover:bg-oat"
          >
            <XMarkIcon
              className="h-6 w-6"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        </div>
        {/* min-h-0 is what lets this shrink below its content inside the
            column flex parent, which is what makes it scroll rather than
            overflow once the card reaches its max height. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-7 2xs:px-5 sm:px-8 sm:py-8">
          {children}
        </div>
      </div>
    </div>
  );
}
